import { isPlannedMaintenanceError } from "../http/serviceAvailability";
import { isStaleWebpackRuntimeError } from "./staleDeploymentRecovery";

/**
 * Returns true when an error should be ignored by Flare.
 *
 * These errors are caused by third-party scripts, browser extensions, external
 * DOM mutation, browser-level network noise, or a stale webpack runtime left
 * over from the previous deployment — not actionable application bugs.
 */
type FlareErrorFilterOptions = {
  source?: string;
  filterTransientBrowserErrors?: boolean;
  filterExpectedRequestErrors?: boolean;
};

export function shouldFilterFlareError(
  error: Error | null,
  message?: string,
  options: string | FlareErrorFilterOptions = {},
): boolean {
  const errorMessage = error?.message || message || "";
  const sourcePath =
    typeof options === "string" ? options : options.source || "";
  const errorStack = error?.stack || "";
  const errorSource = `${sourcePath}\n${errorStack}`;
  const filterTransientBrowserErrors =
    typeof options === "string"
      ? false
      : options.filterTransientBrowserErrors === true;
  const filterExpectedRequestErrors =
    typeof options === "string"
      ? false
      : options.filterExpectedRequestErrors === true;

  if (isPlannedMaintenanceError(error)) {
    return true;
  }

  if (isClarityUetUnshiftError(errorMessage, errorSource)) {
    return true;
  }

  if (isClaritySequenceError(errorMessage, errorSource)) {
    return true;
  }

  if (isReactHookFormInvalidInstanceofError(errorMessage, errorSource)) {
    return true;
  }

  if (isInjectedReactAnchorSerializationError(errorMessage)) {
    return true;
  }

  if (isCefSharpObjectRepositoryError(errorMessage)) {
    return true;
  }

  if (errorMessage === "Script error.") {
    return true;
  }

  if (errorMessage === "JSONP request failed.") {
    return true;
  }

  if (!errorMessage) {
    return true;
  }

  if (errorMessage.includes("Unexpected token")) {
    return true;
  }

  if (filterTransientBrowserErrors && isTransientRequestError(errorMessage)) {
    return true;
  }

  if (
    filterExpectedRequestErrors &&
    (isTransientRequestError(errorMessage) ||
      errorMessage === "Request failed with status code 404" ||
      errorMessage === "Request failed with status code 422")
  ) {
    return true;
  }

  if (
    errorMessage.includes("Minified React error #422") ||
    errorMessage.includes("Minified React error #425") ||
    errorMessage.includes("invariant=422") ||
    errorMessage.includes("invariant=425") ||
    errorMessage.includes("Minified React error #418") ||
    errorMessage.includes("Minified React error #423") ||
    errorMessage.includes("invariant=418") ||
    errorMessage.includes("invariant=423")
  ) {
    return true;
  }

  if (
    errorMessage.includes("Failed to execute 'insertBefore' on 'Node'") ||
    errorMessage.includes("Failed to execute 'removeChild' on 'Node'") ||
    errorMessage.includes("is not a child of this node")
  ) {
    return true;
  }

  if (
    errorMessage.includes("window.webkit.messageHandlers") ||
    errorMessage.includes(
      "Cannot read properties of undefined (reading 'messageHandlers')",
    ) ||
    errorMessage.includes("Error invoking postMessage: Java object is gone") ||
    errorMessage === "Can't find variable: _AutofillCallbackHandler" ||
    errorMessage === "Can't find variable: __firefox__" ||
    (errorMessage.includes("undefined is not an object") &&
      errorMessage.includes("window.__firefox__."))
  ) {
    return true;
  }

  if (errorMessage.includes("getPercent is not defined")) {
    return true;
  }

  if (
    (errorMessage.includes("Loading chunk") ||
      errorMessage.includes("Loading CSS chunk")) &&
    errorMessage.includes("failed")
  ) {
    return true;
  }

  if (
    errorMessage.includes("Access is denied for this document") ||
    errorMessage.includes("The operation is insecure") ||
    errorMessage.includes(
      "getLayoutMap() must be called from a top-level browsing context",
    ) ||
    errorMessage.includes("window.localStorage.getItem") ||
    errorMessage.includes("window.sessionStorage.getItem")
  ) {
    return true;
  }

  if (
    errorMessage.includes("Blocked a frame with origin") &&
    errorMessage.includes("accessing a cross-origin frame")
  ) {
    return true;
  }

  if (
    errorMessage.includes("The object can not be found here") ||
    errorMessage.includes("Identifier 'change_ua' has already been declared")
  ) {
    return true;
  }

  if (
    errorMessage ===
    "ResizeObserver loop completed with undelivered notifications."
  ) {
    return true;
  }

  // Chrome reports a missing webpack module factory as
  // "reading 'call'". That wording is shared with real bugs, so the helper
  // only drops it when the throw site is webpack-*.js. Safari's `l[e]`
  // wording is already unique to the minified runtime.
  if (isStaleWebpackRuntimeError(errorMessage, errorSource)) {
    return true;
  }

  if (
    errorMessage.includes("scrollSpyContainers") &&
    errorMessage.includes("spyCallbacks")
  ) {
    return true;
  }

  if (errorMessage.includes("changedTouches is not iterable")) {
    return true;
  }

  if (
    errorMessage.includes("undefined is not an object") &&
    errorMessage.includes("elt.href")
  ) {
    return true;
  }

  return false;
}

function isClarityUetUnshiftError(
  errorMessage: string,
  errorSource: string,
): boolean {
  return (
    errorSource.includes("clarity.ms/tag/uet/") &&
    errorMessage.includes("Cannot read properties of undefined") &&
    errorMessage.includes("unshift")
  );
}

function isClaritySequenceError(
  errorMessage: string,
  errorSource: string,
): boolean {
  return (
    errorSource.includes("scripts.clarity.ms/") &&
    errorMessage === "Cannot read properties of null (reading 'sequence')"
  );
}

function isReactHookFormInvalidInstanceofError(
  errorMessage: string,
  errorSource: string,
): boolean {
  return (
    errorMessage === "Right-hand side of 'instanceof' is not an object" &&
    errorSource.includes("react-hook-form/dist/index.esm.mjs")
  );
}

function isInjectedReactAnchorSerializationError(
  errorMessage: string,
): boolean {
  return (
    errorMessage.startsWith("Converting circular structure to JSON") &&
    errorMessage.includes("object with constructor 'HTMLAnchorElement'") &&
    /property '__reactFiber\$[^']+'/.test(errorMessage) &&
    errorMessage.includes("property 'stateNode' closes the circle")
  );
}

function isCefSharpObjectRepositoryError(errorMessage: string): boolean {
  return /^Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/.test(
    errorMessage,
  );
}

function isTransientRequestError(errorMessage: string): boolean {
  return (
    errorMessage === "Network Error" ||
    errorMessage === "Request aborted" ||
    errorMessage === "timeout exceeded" ||
    errorMessage === "NetworkError: A network error occurred."
  );
}
